import React, { useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AlertTriangle, Phone, MessageSquare } from 'lucide-react';
import SEO from '../components/SEO';

const CrisisResources = () => {
  const [activeState, setActiveState] = useState('georgia');

  const states = [
    { id: 'georgia', name: 'Georgia' },
    { id: 'maryland', name: 'Maryland' },
    { id: 'tennessee', name: 'Tennessee' },
    { id: 'dc', name: 'Washington DC' },
  ];

  const stateHospitals = {
    georgia: [
      {
        area: 'Atlanta Area',
        hospitals: [
          { name: 'Peachford Hospital', address: '2151 Peachford Rd, Atlanta, GA 30338', phone: '770-455-3200' },
          { name: 'Ridgeview Institute', address: '3995 South Cobb Dr SE, Smyrna, GA 30080', phone: '770-434-4567' },
          { name: 'Emory Healthcare - Wesley Woods', address: '1821 Clifton Rd NE, Atlanta, GA 30329', phone: '404-728-6200' },
          { name: 'Skyland Trail', address: '1961 N Druid Hills Rd NE, Atlanta, GA 30329', phone: '404-315-3633' },
        ],
      },
      {
        area: 'Georgia Crisis Lines',
        hospitals: [
          { name: 'Georgia Crisis & Access Line (GCAL)', address: '24/7 crisis support and referrals', phone: '1-800-715-4225' },
        ],
      },
    ],
    maryland: [
      {
        area: 'Baltimore Area',
        hospitals: [
          { name: 'Sheppard Pratt Health System', address: '6501 N Charles St, Baltimore, MD 21204', phone: '410-938-3000' },
          { name: 'Johns Hopkins Bayview Medical Center', address: '4940 Eastern Ave, Baltimore, MD 21224', phone: '410-550-0100' },
          { name: 'University of Maryland Medical Center', address: '22 S Greene St, Baltimore, MD 21201', phone: '410-328-8667' },
          { name: 'Brook Lane Health Services', address: '13218 Brook Ln, Hagerstown, MD 21742', phone: '301-733-0330' },
        ],
      },
      {
        area: 'Maryland Crisis Hotlines',
        hospitals: [
          { name: 'Maryland Crisis Hotline', address: '24/7 crisis support', phone: '211 or 1-800-422-0009' },
          { name: 'Baltimore Crisis Response', address: '', phone: '410-433-5175' },
        ],
      },
    ],
    tennessee: [
      {
        area: 'Tennessee Crisis Lines',
        hospitals: [
          { name: '988 Suicide & Crisis Lifeline', address: 'Press 0 for local TN counselor - 24/7 support', phone: '988' },
          { name: 'Tennessee Mental Health Hotline', address: '24/7 statewide support', phone: '1-866-903-3787 or 1-855-274-7471' },
          { name: 'Crisis Text Line', address: 'Text TN or NAMI', phone: '741741' },
        ],
      },
      {
        area: 'Memphis Area',
        hospitals: [
          { name: 'Memphis Crisis Center', address: '24/7 crisis intervention', phone: '1-901-274-7477' },
          { name: 'Parkwood Behavioral Health', address: 'Memphis area psychiatric services', phone: '1-901-521-1400' },
        ],
      },
      {
        area: 'Northeast Tennessee',
        hospitals: [
          { name: 'Frontier Health Turning Point', address: 'Johnson City - Ages 7+ walk-in crisis center', phone: '423-926-0940 or 877-928-9062' },
        ],
      },
      {
        area: 'Nashville Area',
        hospitals: [
          { name: 'Mental Health Cooperative', address: 'Crisis services and support', phone: '615-726-0125' },
        ],
      },
    ],
    dc: [
      {
        area: 'DC Crisis Hotlines',
        hospitals: [
          { name: 'DC Dept of Behavioral Health Access Helpline', address: '24/7 crisis support, mobile crisis teams', phone: '1-888-793-4357 (1-888-7WE-HELP)' },
          { name: 'DC Comprehensive Psychiatric Emergency Program (CPEP)', address: '1905 E Street SE, Building 14', phone: '202-673-9319' },
          { name: 'ChAMPS (Child & Adolescent Mobile Psychiatric Service)', address: '1001 Lawrence Street NE - Youth crisis support', phone: '202-481-1440' },
          { name: 'DC Assessment and Referral Center (ARC)', address: '75 P St. NE', phone: '202-727-8473' },
        ],
      },
      {
        area: 'DC Hospital Emergency Services',
        hospitals: [
          { name: 'Psychiatric Institute of Washington', address: 'Washington DC', phone: '202-885-5610' },
          { name: 'MedStar Washington Hospital Center', address: '110 Irving St. NW', phone: '202-537-4080' },
          { name: 'George Washington University Hospital', address: '3800 Reservoir Rd. NW', phone: '855-633-0364' },
          { name: 'Sibley Memorial Hospital', address: '5255 Loughboro Rd. NW', phone: '855-546-1686' },
        ],
      },
    ],
  };

  return (
    <main className="pt-24 bg-[#FDFDFB]">
      <SEO 
        title="Crisis Resources - Immediate Mental Health Help"
        description="24/7 crisis resources for Tennessee, Georgia, Maryland, and DC. Suicide prevention, crisis hotlines, psychiatric emergency services, and hospital locations."
        keywords="mental health crisis Tennessee Georgia Maryland DC, suicide prevention hotline 988, psychiatric emergency, crisis intervention, mental health hospital"
      />
      {/* Emergency Banner */}
      <section className="bg-gradient-to-r from-red-600 to-red-700 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <AlertTriangle className="mx-auto mb-4" size={64} />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">IMMEDIATE EMERGENCY</h1>
            <p className="text-2xl mb-6">If you or someone else is in immediate danger:</p>
            <div className="flex flex-col md:flex-row gap-4 justify-center mb-6">
              <a href="tel:911">
                <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 text-3xl px-12 py-8 font-bold">
                  CALL 911
                </Button>
              </a>
              <a href="tel:988">
                <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 text-3xl px-12 py-8 font-bold">
                  CALL OR TEXT 988
                </Button>
              </a>
            </div>
            <p className="text-xl">988 Suicide & Crisis Lifeline - Free, confidential support 24/7</p>
          </div>
        </div>
      </section>

      {/* National Resources */}
      <section className="py-16 bg-[#FDFDFB]">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-[#7a8a72] mb-8 text-center">National Crisis Resources</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* LGBTQ+ Support */}
              <Card className="p-6 hover-lift bg-white border-[#d4cec5]">
                <h3 className="text-xl font-bold text-[#7a8a72] mb-4">LGBTQ+ Support</h3>
                <div className="space-y-3">
                  <div>
                    <p className="font-semibold text-[#7a8a72]">The Trevor Project</p>
                    <p className="text-sm text-gray-600 mb-2">LGBTQ+ youth (ages 13-24)</p>
                    <a href="tel:1-866-488-7386" className="text-[#7a8a72] hover:underline block">
                      <Phone className="inline mr-1" size={14} />
                      1-866-488-7386
                    </a>
                    <a href="sms:678678" className="text-[#7a8a72] hover:underline block">
                      <MessageSquare className="inline mr-1" size={14} />
                      Text START to 678678
                    </a>
                  </div>
                </div>
              </Card>

              {/* Substance Use */}
              <Card className="p-6 hover-lift">
                <h3 className="text-xl font-bold text-[#7a8a72] mb-4">Substance Use Support</h3>
                <div className="space-y-3">
                  <div>
                    <p className="font-semibold text-[#7a8a72]">SAMHSA National Helpline</p>
                    <p className="text-sm text-gray-600 mb-2">24/7 treatment referral</p>
                    <a href="tel:1-800-662-4357" className="text-[#7a8a72] hover:underline block">
                      <Phone className="inline mr-1" size={14} />
                      1-800-662-HELP (4357)
                    </a>
                  </div>
                </div>
              </Card>

              {/* Crisis Text Line */}
              <Card className="p-6 hover-lift">
                <h3 className="text-xl font-bold text-[#7a8a72] mb-4">Crisis Text & Chat</h3>
                <div className="space-y-3">
                  <div>
                    <p className="font-semibold text-[#7a8a72]">Crisis Text Line</p>
                    <a href="sms:741741" className="text-[#7a8a72] hover:underline block">
                      <MessageSquare className="inline mr-1" size={14} />
                      Text HOME to 741741
                    </a>
                  </div>
                  <div>
                    <p className="font-semibold text-[#7a8a72]">Veterans Crisis Line</p>
                    <a href="tel:988" className="text-[#7a8a72] hover:underline block">
                      <Phone className="inline mr-1" size={14} />
                      Call 988, press 1
                    </a>
                    <a href="sms:838255" className="text-[#7a8a72] hover:underline block">
                      <MessageSquare className="inline mr-1" size={14} />
                      Text 838255
                    </a>
                  </div>
                </div>
              </Card>

              {/* Domestic Violence */}
              <Card className="p-6 hover-lift">
                <h3 className="text-xl font-bold text-[#7a8a72] mb-4">Domestic Violence</h3>
                <div className="space-y-3">
                  <div>
                    <p className="font-semibold text-[#7a8a72]">National Hotline</p>
                    <a href="tel:1-800-799-7233" className="text-[#7a8a72] hover:underline block">
                      <Phone className="inline mr-1" size={14} />
                      1-800-799-SAFE (7233)
                    </a>
                    <a href="sms:22522" className="text-[#7a8a72] hover:underline block">
                      <MessageSquare className="inline mr-1" size={14} />
                      Text START to 22522
                    </a>
                  </div>
                </div>
              </Card>

              {/* Disaster Distress */}
              <Card className="p-6 hover-lift">
                <h3 className="text-xl font-bold text-[#7a8a72] mb-4">Disaster Distress</h3>
                <div className="space-y-3">
                  <div>
                    <p className="font-semibold text-[#7a8a72]">Disaster Distress Helpline</p>
                    <a href="tel:1-800-985-5990" className="text-[#7a8a72] hover:underline block">
                      <Phone className="inline mr-1" size={14} />
                      1-800-985-5990
                    </a>
                    <a href="sms:66746" className="text-[#7a8a72] hover:underline block">
                      <MessageSquare className="inline mr-1" size={14} />
                      Text TalkWithUs to 66746
                    </a>
                  </div>
                </div>
              </Card>

              {/* Postpartum Support */}
              <Card className="p-6 hover-lift">
                <h3 className="text-xl font-bold text-[#7a8a72] mb-4">Postpartum Support</h3>
                <div className="space-y-3">
                  <div>
                    <p className="font-semibold text-[#7a8a72]">Postpartum Support International</p>
                    <a href="tel:1-800-944-4773" className="text-[#7a8a72] hover:underline block">
                      <Phone className="inline mr-1" size={14} />
                      1-800-944-4PPD (4773)
                    </a>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* State-Specific Resources */}
      <section className="py-16 bg-[#f7f4f0]">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-[#7a8a72] mb-8 text-center">
              Psychiatric Hospitals & Crisis Centers
            </h2>

            {/* State Tabs */}
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {states.map((state) => (
                <Button
                  key={state.id}
                  onClick={() => setActiveState(state.id)}
                  className={`${
                    activeState === state.id
                      ? 'bg-[#7a8a72] text-white'
                      : 'bg-white text-[#7a8a72] border-2 border-[#7a8a72]'
                  } hover:bg-[#7a8a72] hover:text-white`}
                >
                  {state.name}
                </Button>
              ))}
            </div>

            {/* State Content */}
            <div className="space-y-6">
              {stateHospitals[activeState]?.map((section, idx) => (
                <Card key={idx} className="p-6">
                  <h3 className="text-2xl font-bold text-[#7a8a72] mb-4">{section.area}</h3>
                  <div className="space-y-4">
                    {section.hospitals.map((hospital, hospIdx) => (
                      <div key={hospIdx} className="border-l-4 border-[#7a8a72] pl-4 py-2">
                        <p className="font-semibold text-[#7a8a72] text-lg">{hospital.name}</p>
                        {hospital.address && <p className="text-gray-600">{hospital.address}</p>}
                        <a
                          href={`tel:${hospital.phone}`}
                          className="text-[#7a8a72] hover:underline font-semibold text-lg"
                        >
                          <Phone className="inline mr-2" size={16} />
                          {hospital.phone}
                        </a>
                      </div>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* You Matter Section */}
      <section className="py-16 bg-gradient-to-b from-white to-[#f0f7f4]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="p-8 md:p-12 bg-gradient-to-br from-[#7a8a72] to-[#1a5f37] text-white text-center">
              <h2 className="text-4xl font-bold mb-6">You Matter</h2>
              <p className="text-xl mb-6">
                If you're in crisis, please reach out. These resources are here for you 24/7. You don't have
                to face this alone. Help is available, and recovery is possible.
              </p>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 mb-6">
                <p className="text-lg mb-4">
                  Our team at Harmony Behavioral & Psychiatric Health is also here for you.
                </p>
                <a href="tel:404-738-7542">
                  <Button size="lg" className="bg-white text-[#7a8a72] hover:bg-gray-100 text-2xl px-8 py-6">
                    <Phone className="mr-2" size={24} />
                    Call or Text: 404-738-7542
                  </Button>
                </a>
              </div>
            </Card>
          </div>
        </div>
      </section>
    </main>
  );
};

export default CrisisResources;